import { NgForOf, UpperCasePipe, AsyncPipe, NgClass, NgIf, isPlatformBrowser, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, ViewChild, ElementRef, QueryList, ChangeDetectorRef, OnDestroy, NgZone, inject, Injectable, PLATFORM_ID, OnInit, signal, Injector, TemplateRef } from '@angular/core';
import { FormControl, ReactiveFormsModule, FormGroup } from '@angular/forms';
import { TuiAvatar, TuiSwitch, TuiBlock, TuiFade, TuiRadio, TuiBadgeNotification, TuiSegmented, TuiChip, TuiTooltip, TuiPassword, TuiInputPin, TuiBadge, TuiStatus, TuiPreview, TuiPreviewDialogService, TuiPreviewTitle, TuiPreviewPagination, TuiPreviewAction } from '@taiga-ui/kit';
import { TuiButton, TuiTitle, TuiFallbackSrcPipe, TuiIcon, TuiAppearance, TuiFormatNumberPipe, TuiGroup, TuiLink, TuiNotification, TuiTextfield, TuiAutoColorPipe, TuiError, TuiFormatDatePipe, TuiFormatDateService, TuiLoader, TuiDialogService } from '@taiga-ui/core';
import { PolymorpheusComponent, PolymorpheusOutlet, PolymorpheusTemplate, type PolymorpheusContent } from '@taiga-ui/polymorpheus';
import { SignOutConfirmationDialog } from '../../../shared/components/sign-out-confirmation-dialog/sign-out-confirmation-dialog';
import { TuiHeader, TuiNavigation, TuiCardMedium, TuiCardLarge, TuiForm, TuiCell, TuiBlockStatus } from '@taiga-ui/layout';
import { TuiTagModule } from '@taiga-ui/legacy';
import { TuiTable } from '@taiga-ui/addon-table';
import { FormsModule } from '@angular/forms';
import { NotificationService } from '../../../shared/services/notification.service';
import { TuiInputModule, TuiTextfieldControllerModule } from '@taiga-ui/legacy';
import { formatDistance, format } from 'date-fns';
import { ApiService } from '../../../shared/services/api.service';
import { TimezoneService } from '../../../shared/services/timezone.service';
import { Router } from '@angular/router';
import { RoleService } from '../../../shared/services/role.service';
import type { Observable } from 'rxjs';
import { map, of, timer, Subscription } from 'rxjs';
import { PermissionDisableDirective } from '../../../shared/directives/permission-disable.directive';
import { DeviceIdentifier } from '../../../shared/utils/device-identifier.util';
import { InputHeading } from '@app/shared/components/input-heading/input-heading';
export interface DeviceData {
  id: string;
  session_id: string;
  device: {
    name: string;
    browser: string;
    ip: string;
    icon: string;
  };
  location: string;
  firstSignIn: Date;
  lastActivity: Date;
  isActive: boolean;
  isCurrent: boolean;
}

@Injectable()
export class FormatService extends TuiFormatDateService {
  private readonly injector = inject(Injector);
  private readonly delay$ = isPlatformBrowser(inject(PLATFORM_ID))
    ? timer(0, 1000)
    : of(0);

  constructor(private timezoneService: TimezoneService) {
    super();
  }

  public override format(timestamp: number): Observable<string> {
    return this.delay$.pipe(map(() => {
      const date = new Date(timestamp);
      return this.timezoneService.formatDateTime(date, {
        format: 'short',
        show_timezone: false,
        show_time: false
      });
    }));
  }
}

@Component({
  selector: 'app-profile-setting',
  standalone: true,
  imports: [
    TuiAvatar,
    TuiHeader,
    TuiTitle,
    TuiButton,
    AsyncPipe,
    DatePipe,
    TuiFallbackSrcPipe,
    TuiIcon,
    TuiAppearance,
    TuiTagModule,
    TuiCardLarge,
    TuiNavigation,
    TuiForm,
    TuiTable,
    NgForOf,
    TuiSwitch,
    TuiCell,
    ReactiveFormsModule,
    TuiRadio,
    TuiGroup,
    TuiSegmented,
    TuiLink,
    TuiChip,
    FormsModule,
    TuiNotification,
    NgIf,
    TuiTextfield,
    TuiTooltip,
    TuiInputModule,
    TuiTextfieldControllerModule,
    TuiError,
    TuiPassword,
    TuiInputPin,
    TuiFormatDatePipe,
    PermissionDisableDirective,
    TuiBlockStatus,
    TuiLoader,
    TuiBadge,
    TuiStatus,
    TuiPreview,
    TuiPreviewTitle,
    TuiPreviewPagination,
    TuiPreviewAction,
    PolymorpheusOutlet,
    PolymorpheusTemplate,
    InputHeading
  ],
  templateUrl: './profile-setting.html',
  styleUrl: './profile-setting.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: TuiFormatDateService,
      useClass: FormatService,
    },
  ],
})
export class ProfileSetting implements OnInit, OnDestroy {
  private readonly injector = inject(Injector);
  protected value = 'Test';
  protected tag = 'Super Admin';
  protected twoFactorControl = new FormControl(true);
  protected isEditMode = false;
  protected isPasswordEditMode = false;
  protected isLoading = true;
  currentUser: any = null;
  currentOrganization: any = null;
  private roleSubscription: Subscription = new Subscription();
  protected selectedUserAvatar: string | ArrayBuffer | null = null;
  protected isUserAvatarEditMode = false;
  protected isLogoUploading = false;
  protected logoUploadProgress = 0;
  protected showSuccessCheck = false;
  protected orgLogoFile: File | null = null;
  protected avatarSizeError: string = '';

  @ViewChild('firstInput', { static: false }) firstInput!: ElementRef;
  @ViewChild('userAvatarFileInput', { static: false }) userAvatarFileInput!: ElementRef;
  @ViewChild('logoPreview') protected readonly logoPreview?: TemplateRef<any>;
  @ViewChild('emailUpdateDialog') protected readonly emailUpdateDialog?: TemplateRef<any>;

  protected readonly previewService = inject(TuiPreviewDialogService);

  protected data = [
    {
      key: 'Name',
      value: '',
      isEditing: false,
      isReadOnly: false,
    },
    {
      key: 'Email',
      value: '',
      isEditing: false,
      isReadOnly: true,
    },
    {
      key: 'Phone',
      value: '',
      countryCode: '+1',
      isEditing: false,
      isReadOnly: false,
    },
    {
      key: 'Company',
      value: '',
      isEditing: false,
      isReadOnly: true,
    },
  ];

  protected readonly columns = ['key', 'value'] as const;

  // For Password
  protected dataPassword = [
    {
      key: 'Password',
      value: '************',
      isEditing: false,
      showPassword: false,
      error: '',
    },
  ];

  // Error states for password validation
  protected passwordError = '';
  protected repeatPasswordError = '';


  protected readonly columnsPassword = ['key', 'value'] as const;

  // Active Sessions Management
  protected readonly activeSessions = signal<DeviceData[]>([]);
  protected readonly loadingSessions = signal<boolean>(false);
  protected readonly deviceColumns = ['device', 'location', 'last-activity', 'action'] as const;
  protected readonly headerGroupForm = new FormGroup({
    testValue: new FormControl('profile'), // default value
  });

  // OTP form for two-factor authentication
  protected otpForm = new FormGroup({
    otpValue: new FormControl(''),
  });

  // Modal control
  protected showOtpModal: boolean = false;
  protected showEmailUpdateModal: boolean = false;
  protected newEmailValue: string = '';
  protected emailUpdateErrorMessage: string = '';
  protected isEmailUpdating: boolean = false;
  protected generatedOtp = '';
  protected otpSessionId: string | null = null;
  protected modalErrorMessage: string = '';
  protected isSuccessMessage: boolean = false;
  protected is2faPendingToggle: boolean = false;
  protected pending2faValue: boolean = false;
  protected resendTimer: number = 0;
  protected canResendOtp: boolean = true;
  private otpTimer: any;

  constructor(
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef,
    private apiService: ApiService,
    private roleService: RoleService,
    private router: Router,
    private dialogs: TuiDialogService
  ) {
    // Ensure modal is hidden on component initialization
    this.showOtpModal = false;
    this.showEmailUpdateModal = false;
  }

  ngOnInit(): void {
    try {
      this.loadUserData();

      // Load roles from API and then update tag (only if authenticated)
      const currentUser = this.apiService.getCurrentUser();
      if (currentUser && currentUser.id) {
        this.roleService.getRoles().subscribe(roles => {
          // Update tag after roles are loaded
          this.updateTagForCurrentRole();
        });
      }

      // Subscribe to role changes for immediate updates
      this.roleSubscription = this.roleService.currentRole$.subscribe(newRole => {
        this.updateUserDataForRole(newRole);
      });

      // Initialize and handle Two-Factor toggle
      this.setupTwoFactorToggleHandler();

      // Fetch active sessions
      this.fetchSessions();
    } catch (error) {
    }
  }

  protected readonly emptyDisplayValue = 'N/A';

  private isPlaceholderName(value: unknown): boolean {
    const normalized = String(value ?? '').trim();
    return !normalized || /^user$/i.test(normalized);
  }

  protected displayValue(value: unknown): string {
    if (value === null || value === undefined) {
      return this.emptyDisplayValue;
    }

    const normalized = String(value).trim();
    return normalized ? normalized : this.emptyDisplayValue;
  }

  protected displayName(value: unknown): string {
    return this.isPlaceholderName(value) ? this.emptyDisplayValue : String(value).trim();
  }

  private applyEmptyProfileFallback(): void {
    this.data[0].value = this.emptyDisplayValue;
    this.data[1].value = this.emptyDisplayValue;
    this.data[2].value = this.emptyDisplayValue;
    this.data[3].value = this.emptyDisplayValue;
  }

  protected getFormattedCreatedAt(): string {
    const raw = this.currentUser?.createdAt;
    if (!raw) return 'N/A';
    const d = new Date(raw);
    if (isNaN(d.getTime())) return 'N/A';
    return d.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  protected loadUserData(): void {
    try {
      this.currentUser = this.apiService.getCurrentUser();
      if (this.currentUser) {
        const userId = this.currentUser.id || this.currentUser.user_id;

        if (userId) {
          // Fetch fresh user data from API
          this.apiService.getUserById(userId).subscribe({
            next: (response: any) => {
              // Handle response - check different possible response structures
              let userData = null;
              if (response.data) {
                userData = response.data;
              } else if (response) {
                userData = response;
              }

              if (userData) {
                // Preserve role and permissions from original currentUser before merging
                const originalRole = this.currentUser.role;
                const originalPermissions = this.currentUser.role?.permission;

                // Update currentUser with fresh data from API
                this.currentUser = { ...this.currentUser, ...userData };

                // Handle name field from API - split into firstName and lastName
                if (this.currentUser.name && !this.currentUser.firstName) {
                  const nameParts = this.currentUser.name.trim().split(/\s+/);
                  if (nameParts.length === 1) {
                    this.currentUser.firstName = nameParts[0];
                    this.currentUser.lastName = '';
                  } else {
                    this.currentUser.firstName = nameParts[0];
                    this.currentUser.lastName = nameParts.slice(1).join(' ');
                  }
                }

                // Restore role and permissions if they were in original but not in API response
                // This ensures we don't lose permission data when updating user info
                if (originalRole && originalPermissions && (!this.currentUser.role?.permission || this.currentUser.role.permission.length === 0)) {
                  this.currentUser.role = originalRole;
                  // console.log('Preserved role and permissions after user data update');
                }

                // Update localStorage with fresh data (with preserved permissions)
                localStorage.setItem('user_info', JSON.stringify(this.currentUser));

                // Update the data array with user information
                // Use name field directly from API response; show N/A when a field is missing
                this.data[0].value = this.displayName(
                  userData.name || this.currentUser.name || `${this.currentUser.firstName || ''} ${this.currentUser.lastName || ''}`.trim()
                );
                this.data[1].value = this.displayValue(userData.email || this.currentUser.email);
                this.data[2].value = this.displayValue(userData.phone || this.currentUser.phone);
                // Company name fetched from org API — call after user data is ready
                this.loadOrganizationData();

                // Update tag with current role from RoleService
                this.updateTagForCurrentRole();

                this.isLoading = false;
                this.cdr.detectChanges();

                // Initialize 2FA control from user data without emitting change
                const twoFA = !!(this.currentUser.two_factor_enabled);
                this.twoFactorControl.setValue(twoFA, { emitEvent: false });
              }
            },
            error: (error) => {
              console.error('Error loading user data from API:', error);
              // Fallback to localStorage data
              this.loadUserDataFromLocalStorage();
            }
          });
        } else {
          // No user ID available, load from localStorage
          this.loadUserDataFromLocalStorage();
        }
      } else {
        // Set default values if no user data
        this.applyEmptyProfileFallback();
        this.tag = 'User';
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    } catch (error) {
      console.error('Error in loadUserData:', error);
      // Set default values on error
      this.applyEmptyProfileFallback();
      this.tag = 'User';
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }

  private loadUserDataFromLocalStorage(): void {
    // Update the data array with user information from localStorage
    const fullName = this.currentUser.name
      || `${this.currentUser.firstName || ''} ${this.currentUser.lastName || ''}`.trim();
    this.data[0].value = this.displayName(fullName);
    this.data[1].value = this.displayValue(this.currentUser.email);
    this.data[2].value = this.displayValue(this.currentUser.phone);
    // Company fetched from org API
    this.loadOrganizationData();

    // Update tag with current role from RoleService
    this.updateTagForCurrentRole();

    this.isLoading = false;
    // Force change detection
    this.cdr.detectChanges();

    // Initialize 2FA toggle from local data
    const twoFA = !!(this.currentUser.two_factor_enabled);
    this.twoFactorControl.setValue(twoFA, { emitEvent: false });
  }

  protected loadOrganizationData(): void {
    try {
      const orgId = this.apiService.getCurrentOrganizationId();

      if (!orgId) {
        this.data[3].value = this.displayValue(this.currentUser?.company);
        return;
      }

      this.apiService.getOrganization(orgId).subscribe({
        next: (response) => {
          let organizationName = '';

          // extractData already unwraps response.data, so response is the org object directly
          if (response && typeof response === 'object') {
            const org = Array.isArray(response) ? response[0] : response;
            organizationName = org?.name || org?.company || org?.data?.name || '';
            if (org && !Array.isArray(response)) {
              this.currentOrganization = org;
            }
          }

          // Fallback to currentUser.company if API didn't return a name
          this.data[3].value = this.displayValue(organizationName || this.currentUser?.company);
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error loading organization data:', error);
          this.data[3].value = this.displayValue(this.currentUser?.company);
          this.cdr.detectChanges();
        }
      });
    } catch (error) {
      console.error('Error in loadOrganizationData:', error);
      this.data[3].value = this.displayValue(this.currentUser?.company);
    }
  }

  private setupTwoFactorToggleHandler(): void {
    // Ensure initial value reflects stored user setting
    const storedUser = this.apiService.getCurrentUser();
    if (storedUser) {
      this.twoFactorControl.setValue(!!storedUser.two_factor_enabled, { emitEvent: false });
    }

    this.twoFactorControl.valueChanges.subscribe((enabled) => {
      // If we are currently in middle of an OTP verification, don't trigger again
      if (this.is2faPendingToggle) return;

      const userId = this.currentUser?.id || this.currentUser?.user_id;
      if (!userId) {
        this.notificationService.showError('User not found. Please re-login.', 3000);
        return;
      }

      // Capture the original value to revert if canceled
      const previousValue = !enabled;

      const email = this.currentUser?.email || '';
      if (!email.trim() || email === 'null' || email === 'undefined') {
        this.twoFactorControl.setValue(previousValue, { emitEvent: false });
        this.openEmailUpdateModal();
        return;
      }

      // Start verification flow
      this.is2faPendingToggle = true;
      this.pending2faValue = !!enabled;

      this.isLoading = true;
      this.modalErrorMessage = '';
      this.isSuccessMessage = false;

      this.apiService.generateForgotPassword(email).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          const isSuccess = response.success || response['Status'] === 'Success' || response['status'] === 'Success';
          const details = response['Details'] || response['details'] || response['data'];

          if (isSuccess) {
            this.otpSessionId = (typeof details === 'string' && details) ? details : email;
            this.openOtpModal();
            this.startResendTimer();
            // We keep the toggle in the new state, but don't save yet. 
            // If they cancel, we revert it in closeOtpModal.
          } else {
            // Revert toggle on API error
            this.is2faPendingToggle = false;
            this.twoFactorControl.setValue(previousValue, { emitEvent: false });
            this.notificationService.showError(response.message || 'Verification failed to start. Please try again.', 3000);
          }
          this.cdr.detectChanges();
        },
        error: (error: any) => {
          this.isLoading = false;
          this.is2faPendingToggle = false;
          this.twoFactorControl.setValue(previousValue, { emitEvent: false });

          const apiError = error?.error;
          if (apiError && (apiError.message === 'Please provide a valid email address' || apiError.statusCode === 400)) {
            this.openEmailUpdateModal();
          } else {
            this.notificationService.showError('Failed to initiate security verification.', 3000);
          }
          this.cdr.detectChanges();
        }
      });
    });
  }

  private updateUserDataForRole(newRole: string): void {

    // Update tag immediately
    this.updateTagForRole(newRole);

    // Force change detection
    this.cdr.detectChanges();
  }

  private updateTagForCurrentRole(): void {
    const currentRole = this.roleService.getCurrentRole();
    this.updateTagForRole(currentRole);
  }

  private updateTagForRole(role: string): void {

    // Update tag with role name using normalizeRole
    const normalizedRole = this.roleService.normalizeRole({
      roleId: this.currentUser?.role_id,
      roleName: role,
      type: this.currentUser?.type,
      email: this.currentUser?.email
    });

    this.tag = normalizedRole.charAt(0).toUpperCase() + normalizedRole.slice(1);

    // Force change detection
    this.cdr.detectChanges();
  }

  protected getUserInitials(): string {
    if (!this.currentUser) return 'U';

    const fullName = this.currentUser.name
      || `${this.currentUser.firstName || ''} ${this.currentUser.lastName || ''}`.trim();
    if (this.isPlaceholderName(fullName)) {
      return 'U';
    }

    const firstName = this.isPlaceholderName(this.currentUser.firstName) ? '' : (this.currentUser.firstName || '');
    const lastName = this.currentUser.lastName || '';

    const firstInitial = firstName.charAt(0).toUpperCase();
    const lastInitial = lastName.charAt(0).toUpperCase();

    if (firstName && lastName) {
      return firstInitial + lastInitial;
    } else if (firstName) {
      return firstInitial;
    } else if (lastName) {
      return lastInitial;
    }

    return 'U';
  }

  protected getUserAvatarSrc(): string {
    // Return profile_image from user data if available
    return this.currentUser?.profile_image || this.currentUser?.avatarUrl || '';
  }

  protected getAvatarSrc(): string {
    if (this.selectedUserAvatar && typeof this.selectedUserAvatar === 'string') {
      return this.selectedUserAvatar;
    }
    return this.getUserAvatarSrc();
  }

  protected hasAvatar(): boolean {
    return !!(this.selectedUserAvatar || this.getUserAvatarSrc());
  }

  protected onValueChange(newValue: string, index: number): void {
    this.data[index].value = newValue;
  }

  protected toggleEditMode(): void {
    this.isEditMode = !this.isEditMode;
    if (!this.isEditMode) {
      // Save mode - turn off editing for all fields and save data
      this.data.forEach(item => {
        item.isEditing = false;
      });

      // Prepare data for API call — don't persist the N/A placeholder
      const nameValue = this.data[0].value === this.emptyDisplayValue ? '' : (this.data[0].value || '');
      const emailValue = this.data[1].value === this.emptyDisplayValue ? '' : (this.data[1].value || '');
      const phoneValue = this.data[2].value === this.emptyDisplayValue ? '' : (this.data[2].value || '');

      const userData = {
        name: nameValue.trim(),
        phone: phoneValue,
        email: emailValue,
        id: this.currentUser?.id || this.currentUser?.user_id
      };

      // Call API to update user data
      this.apiService.updateUser(userData).subscribe({
        next: (response: any) => {
          // Update current user object - parse name back to firstName/lastName for storage
          const nameParts = nameValue.trim().split(' ');
          this.currentUser.name = nameValue.trim();
          this.currentUser.firstName = nameParts[0] || '';
          this.currentUser.lastName = nameParts.slice(1).join(' ') || '';
          this.currentUser.phone = phoneValue;

          // Reload user data to reflect changes
          this.loadUserData();

          // Also reload organization data to ensure company name is refreshed
          this.loadOrganizationData();

          this.notificationService.showSuccess(
            response.message || 'Profile updated successfully!',
            2000
          );
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error updating profile:', error);
          this.notificationService.showError(
            'Failed to update profile. Please try again.',
            3000
          );
          // Revert to edit mode on error
          this.isEditMode = true;
          this.data.forEach((item: any) => {
            if (!item.isReadOnly) {
              item.isEditing = true;
            }
          });
          this.cdr.detectChanges();
        }
      });
    } else {
      // Edit mode - turn on editing for all fields except read-only fields
      this.data.forEach((item: any) => {
        if (!item.isReadOnly) {
          item.isEditing = true;
          if (item.value === this.emptyDisplayValue) {
            item.value = '';
          }
        }
      });

      // Focus on the first editable input after a short delay
      setTimeout(() => {
        if (this.firstInput) {
          this.firstInput.nativeElement.focus();
        }
      }, 100);
    }
  }

  protected cancelEditMode(): void {
    this.isEditMode = false;
    // Reset all fields to current user's original values
    this.data.forEach(item => {
      item.isEditing = false;
    });

    // Reload user data to restore original values
    this.loadUserData();

    this.notificationService.showInfo('Changes cancelled', 2000);
  }

  protected togglePasswordEditMode(index: number): void {
    if (this.isPasswordEditMode) {
      // Save mode - validate and save password changes
      const saveSuccess = this.savePasswordChanges();

      if (saveSuccess) {
        // Only disable edit mode if save was successful
        this.isPasswordEditMode = false;
        this.dataPassword.forEach(item => {
          item.isEditing = false;
        });

        // Remove Confirm field if it exists
        this.dataPassword = this.dataPassword.filter(item => item.key !== 'Confirm');
      }
      // If save failed, stay in edit mode and show errors
    } else {
      // Edit mode - turn on editing and add Confirm field
      this.isPasswordEditMode = true;

      // Add Confirm field if it doesn't exist
      if (!this.dataPassword.find(item => item.key === 'Confirm')) {
        this.dataPassword.push({
          key: 'Confirm',
          value: '',
          isEditing: true,
          showPassword: false,
          error: '',
        });
      }

      // Enable editing for password field and clear the value
      this.dataPassword[0].isEditing = true;
      this.dataPassword[0].value = '';

      // Clear Confirm field as well
      const repeatPasswordField = this.dataPassword.find(item => item.key === 'Confirm');
      if (repeatPasswordField) {
        repeatPasswordField.value = '';
      }
    }
  }

  protected savePasswordChanges(): boolean {
    const passwordField = this.dataPassword.find(item => item.key === 'Password');
    const repeatPasswordField = this.dataPassword.find(item => item.key === 'Confirm');

    // Clear previous errors
    this.passwordError = '';
    this.repeatPasswordError = '';

    if (passwordField && repeatPasswordField) {
      // Validate password length
      if (passwordField.value.length < 6) {
        this.passwordError = 'Password must be at least 6 characters long';
        return false;
      }

      // Check if passwords match
      if (passwordField.value !== repeatPasswordField.value) {
        this.repeatPasswordError = 'Password and Confirm do not match';
        return false;
      }

      // Passwords match and are valid - proceed to save via unified API
      this.isLoading = true;
      const emailOrSession = this.otpSessionId || this.currentUser?.email || '';

      this.apiService.updateForgotPassword(emailOrSession, passwordField.value).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          const isSuccess = response.success || response['Status'] === 'Success' || response['status'] === 'Success' || response['data']?.success;

          if (isSuccess) {
            this.notificationService.showSuccess('Password changed successfully!', 2000);

            // Mask the passwords for display
            if (passwordField) passwordField.value = '********';
            if (repeatPasswordField) repeatPasswordField.value = '********';

            this.isPasswordEditMode = false;
            this.dataPassword.forEach(item => {
              item.isEditing = false;
              item.showPassword = false;
            });
            // Remove Confirm field
            this.dataPassword = this.dataPassword.filter(item => item.key !== 'Confirm');
            this.otpSessionId = null; // Clear session after success
          } else {
            this.passwordError = response.message || 'Failed to update password. Please try again.';
          }
          this.cdr.detectChanges();
        },
        error: (error: any) => {
          this.isLoading = false;
          this.passwordError = 'Failed to update password. Please try again.';
          this.cdr.detectChanges();
        }
      });
      return true;
    } else if (passwordField) {
      // Handle case when only password field exists (no Confirm)
      if (passwordField.value.length < 6) {
        this.passwordError = 'Password must be at least 6 characters long';
        return false;
      }

      this.isLoading = true;
      const emailOrSession = this.otpSessionId || this.currentUser?.email || '';

      this.apiService.updateForgotPassword(emailOrSession, passwordField.value).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          const isSuccess = response.success || response['Status'] === 'Success' || response['status'] === 'Success' || response['data']?.success;

          if (isSuccess) {
            this.notificationService.showSuccess('Password changed successfully!', 2000);
            if (passwordField) passwordField.value = '********';
            this.isPasswordEditMode = false;
            this.dataPassword.forEach(item => {
              item.isEditing = false;
              item.showPassword = false;
            });
            this.otpSessionId = null;
          } else {
            this.passwordError = response.message || 'Failed to update password. Please try again.';
          }
          this.cdr.detectChanges();
        },
        error: (error: any) => {
          this.isLoading = false;
          this.passwordError = 'Failed to update password. Please try again.';
          this.cdr.detectChanges();
        }
      });
      return true;
    }

    return false;
  }

  protected onPasswordValueChange(newValue: string, index: number): void {
    this.dataPassword[index].value = newValue;
    // Only clear the error for the field being edited
    if (this.dataPassword[index].key === 'Password') {
      this.passwordError = '';
    }
    if (this.dataPassword[index].key === 'Confirm') {
      this.repeatPasswordError = '';
    }
  }

  protected togglePasswordVisibility(index: number): void {
    this.dataPassword[index].showPassword = !this.dataPassword[index].showPassword;
  }

  protected cancelPasswordEditMode(): void {
    this.isPasswordEditMode = false;
    // Clear error messages
    this.passwordError = '';
    this.repeatPasswordError = '';
    // Reset password fields to original state
    this.dataPassword.forEach(item => {
      item.isEditing = false;
      item.showPassword = false;
      if (item.key === 'Password') {
        item.value = '********';
      }
    });
    // Remove Confirm field if it exists
    this.dataPassword = this.dataPassword.filter(item => item.key !== 'Confirm');
    this.notificationService.showInfo('Password changes cancelled', 2000);
  }

  // Modal control methods
  protected onChangePasswordClick(): void {
    if (!this.isPasswordEditMode) {
      const email = this.currentUser?.email || '';
      if (!email.trim() || email === 'null' || email === 'undefined') {
        this.openEmailUpdateModal();
        return;
      }

      // Always execute OTP verification flow regardless of 2FA status
      this.isLoading = true;
      this.modalErrorMessage = '';
      this.isSuccessMessage = false;

      this.apiService.generateForgotPassword(email).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          const isSuccess = response.success || response['Status'] === 'Success' || response['status'] === 'Success';
          const details = response['Details'] || response['details'] || response['data'];

          if (isSuccess) {
            this.otpSessionId = (typeof details === 'string' && details) ? details : email;
            this.openOtpModal();
            this.startResendTimer();
          } else {
            this.notificationService.showError(response.message || 'Failed to send OTP. Please try again.', 3000);
          }
          this.cdr.detectChanges();
        },
        error: (error: any) => {
          this.isLoading = false;
          const apiError = error?.error;
          if (apiError && (apiError.message === 'Please provide a valid email address' || apiError.statusCode === 400)) {
            this.openEmailUpdateModal();
          } else {
            this.notificationService.showError('Failed to send OTP. Please check your connection.', 3000);
          }
          this.cdr.detectChanges();
        }
      });
    } else {
      // If already in edit mode, proceed with save
      this.togglePasswordEditMode(0);
    }
  }

  protected generateOtp(): void {
    // Legacy method - functionality moved to API calls
  }

  protected openOtpModal(): void {
    this.showOtpModal = true;
    this.modalErrorMessage = '';
    this.isSuccessMessage = false;
    // Disable body scroll
    document.body.classList.add('modal-open');
  }

  protected openEmailUpdateModal(): void {
    if (!this.emailUpdateDialog) return;

    this.newEmailValue = this.currentUser?.email || '';
    this.emailUpdateErrorMessage = '';
    this.isEmailUpdating = false;

    this.dialogs.open(this.emailUpdateDialog, {
      label: 'Update Email Required',
      size: 's',
      dismissible: true
    }).subscribe();
    this.cdr.detectChanges();
  }

  protected onEmailUpdateSubmit(observer: any): void {
    if (!this.newEmailValue.trim()) {
      this.emailUpdateErrorMessage = 'Please enter a valid email address';
      this.cdr.detectChanges();
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(this.newEmailValue.trim())) {
      this.emailUpdateErrorMessage = 'Please enter a valid email address';
      this.cdr.detectChanges();
      return;
    }

    this.isEmailUpdating = true;
    this.emailUpdateErrorMessage = '';
    this.cdr.detectChanges();

    const userId = this.currentUser?.id || this.currentUser?.user_id;
    const userData = {
      id: userId,
      email: this.newEmailValue.trim()
    };

    this.apiService.updateUser(userData).subscribe({
      next: (response: any) => {
        this.isEmailUpdating = false;

        // Update local state
        if (this.currentUser) {
          this.currentUser.email = this.newEmailValue.trim();
          localStorage.setItem('user_info', JSON.stringify(this.currentUser));
        }
        if (this.data && this.data[1]) {
          this.data[1].value = this.newEmailValue.trim();
        }

        this.notificationService.showSuccess(
          response.message || 'Email updated successfully!',
          2000
        );

        observer.next();
        observer.complete();
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error updating email:', error);
        this.isEmailUpdating = false;
        this.emailUpdateErrorMessage = 'Failed to update email. Please try again.';
        this.cdr.detectChanges();
      }
    });
  }

  private save2faToggleStatus(): void {
    const userId = this.currentUser?.id || this.currentUser?.user_id;
    this.isLoading = true;

    this.apiService.updateUser({
      id: userId,
      email: this.currentUser?.email,
      two_factor_enabled: this.pending2faValue
    }).subscribe({
      next: (response: any) => {
        this.isLoading = false;
        this.is2faPendingToggle = false;
        this.closeOtpModal();
        this.notificationService.showSuccess(
          response?.message || (this.pending2faValue ? 'Two-Factor enabled' : 'Two-Factor disabled'),
          2000
        );
        this.cdr.detectChanges();
      },
      error: () => {
        this.isLoading = false;
        this.is2faPendingToggle = false;
        this.twoFactorControl.setValue(!this.pending2faValue, { emitEvent: false });
        this.closeOtpModal();
        this.notificationService.showError('Failed to update Two-Factor setting', 3000);
        this.cdr.detectChanges();
      }
    });
  }

  protected closeOtpModal(): void {
    if (this.is2faPendingToggle) {
      // Revert toggle UI if canceled mid-verification
      this.twoFactorControl.setValue(!this.pending2faValue, { emitEvent: false });
      this.is2faPendingToggle = false;
    }
    this.showOtpModal = false;
    this.otpForm.reset();
    // Enable body scroll
    document.body.classList.remove('modal-open');
  }

  protected verifyOtp(): void {
    const otpValue = this.otpForm.get('otpValue')?.value;
    if (otpValue && (otpValue.length === 4 || otpValue.length === 6)) {
      this.isLoading = true;
      this.modalErrorMessage = '';
      this.isSuccessMessage = false;

      this.apiService.verifyOtp({ email: this.otpSessionId || this.currentUser?.email || '', otp: String(otpValue), purpose: 'FORGOT_PASSWORD' }).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          // Unified validation for "success: true but data: false" pattern
          const isOtpValid = response?.data !== false &&
            response?.data !== null &&
            !(response?.data?.message && String(response.data.message).toLowerCase().includes('invalid')) &&
            !(response?.data && response.data.data === null && response.data.message);

          if (isOtpValid) {
            if (this.is2faPendingToggle) {
              // Handle 2FA Toggle Verification
              this.save2faToggleStatus();
            } else {
              // Handle Password Change Verification
              this.notificationService.showSuccess('OTP verified successfully!', 2000);
              this.closeOtpModal();
              this.isPasswordEditMode = true;

              // Add Confirm field if it doesn't exist
              if (!this.dataPassword.find(item => item.key === 'Confirm')) {
                this.dataPassword.push({
                  key: 'Confirm',
                  value: '',
                  isEditing: true,
                  showPassword: false,
                  error: '',
                });
              }

              // Enable editing for password field and clear the value
              this.dataPassword[0].isEditing = true;
              this.dataPassword[0].value = '';

              // Clear Confirm field as well
              const repeatPasswordField = this.dataPassword.find(item => item.key === 'Confirm');
              if (repeatPasswordField) {
                repeatPasswordField.value = '';
              }
            }
          } else {
            this.modalErrorMessage = response?.message || response?.data?.message || 'Invalid or Expired OTP. Please try again.';
            this.otpForm.reset();
          }
          this.cdr.detectChanges();
        },
        error: (error: any) => {
          this.isLoading = false;
          this.modalErrorMessage = 'Failed to verify OTP. Please try again.';
          this.cdr.detectChanges();
        }
      });
    } else {
      this.modalErrorMessage = 'Please enter a valid verification code';
    }
  }

  private startResendTimer(): void {
    if (this.otpTimer) {
      clearInterval(this.otpTimer);
    }
    this.canResendOtp = false;
    this.resendTimer = 30; // Standard 30s timer

    this.otpTimer = setInterval(() => {
      this.resendTimer--;
      if (this.resendTimer <= 0) {
        this.canResendOtp = true;
        this.resendTimer = 0;
        clearInterval(this.otpTimer);
      }
      this.cdr.detectChanges();
    }, 1000);
  }

  protected resendOtp(): void {
    this.isLoading = true;
    this.modalErrorMessage = '';
    this.isSuccessMessage = false;

    const email = this.currentUser?.email || '';
    this.apiService.generateForgotPassword(email).subscribe({
      next: (response: any) => {
        this.isLoading = false;
        const isSuccess = response.success || response['Status'] === 'Success' || response['status'] === 'Success';
        const details = response['Details'] || response['details'] || response['data'];

        if (isSuccess) {
          this.modalErrorMessage = 'OTP resent successfully';
          this.isSuccessMessage = true;
          this.otpSessionId = (typeof details === 'string' && details) ? details : email;
          this.otpForm.reset();
          this.startResendTimer();
        } else {
          this.modalErrorMessage = response.message || 'Failed to resend code';
        }
        this.cdr.detectChanges();
      },
      error: (error: any) => {
        this.isLoading = false;
        this.modalErrorMessage = 'Failed to resend code';
        this.cdr.detectChanges();
      }
    });
  }

  // Avatar upload methods
  public triggerUserAvatarFileInput(): void {
    if (this.userAvatarFileInput) {
      this.userAvatarFileInput.nativeElement.click();
    }
  }

  public onUserAvatarFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      this.orgLogoFile = input.files[0];

      // Validate file type
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
      if (!validTypes.includes(this.orgLogoFile.type)) {
        this.avatarSizeError = 'Please select a valid image file (PNG, JPG, or SVG)';
        this.cdr.detectChanges();
        input.value = '';
        return;
      }

      // Validate file size (max 2MB)
      const maxSize = 2 * 1024 * 1024; // 2MB in bytes
      if (this.orgLogoFile.size > maxSize) {
        this.avatarSizeError = 'Image size must be less than 2MB';
        this.cdr.detectChanges();
        input.value = '';
        return;
      }

      this.avatarSizeError = '';

      // Preview
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.selectedUserAvatar = e.target?.result || null;
        this.isUserAvatarEditMode = true;
        this.cdr.detectChanges();

        // Start upload immediately
        this.saveUserAvatar();
      };
      reader.readAsDataURL(this.orgLogoFile);
    }
    input.value = '';
  }

  public saveUserAvatar(): void {
    if (!this.orgLogoFile) {
      this.isUserAvatarEditMode = false;
      return;
    }

    const userId = this.currentUser?.id || this.currentUser?.user_id;
    if (!userId) {
      this.notificationService.showError('User ID not found!', 3000);
      return;
    }

    this.isLogoUploading = true;
    this.logoUploadProgress = 0;
    this.showSuccessCheck = false;
    this.avatarSizeError = ''; // Clear any previous errors when starting new upload
    this.cdr.detectChanges();

    // Start a smooth fake progress increment to ensure animation is visible even for fast uploads
    const progressInterval = setInterval(() => {
      if (this.logoUploadProgress < 90) {
        this.logoUploadProgress += Math.floor(Math.random() * 5) + 2;
        if (this.logoUploadProgress > 90) this.logoUploadProgress = 90;
        this.cdr.detectChanges();
      }
    }, 150);

    // Use uploadUserProfilePicture (assuming it has a progress version or we handle events)
    // Looking at ApiService, we might need to add a withProgress version or just wrap the existing one
    this.apiService.uploadUserProfilePicture(userId, this.orgLogoFile).subscribe({
      next: (response: any) => {
        // Since we don't have a progress specific stream for user profile yet, 
        // we'll rely on the smooth simulation and then jump to 100 on response
        // Update success check to match API response structure
        const imageUrl = response.imageUrl || response.data?.imageUrl;
        if (response.success || response.status || imageUrl) {
          clearInterval(progressInterval);
          this.logoUploadProgress = 100;
          this.showSuccessCheck = true;
          this.cdr.detectChanges();

          // Update current user locally
          if (imageUrl) {
            this.currentUser.profile_image = imageUrl;
            this.currentUser.avatarUrl = imageUrl;

            // Save to localStorage
            try {
              const userInfo = localStorage.getItem('user_info');
              if (userInfo) {
                const userData = JSON.parse(userInfo);
                userData.profile_image = imageUrl;
                userData.avatarUrl = imageUrl;
                localStorage.setItem('user_info', JSON.stringify(userData));
              }
            } catch (e) { }
          }

          // Wait for checkmark animation
          setTimeout(() => {
            this.isLogoUploading = false;
            this.isUserAvatarEditMode = false;
            this.showSuccessCheck = false;
            this.selectedUserAvatar = null;
            this.orgLogoFile = null;
            this.logoUploadProgress = 0;
            this.notificationService.showSuccess(response.message || 'Profile picture updated successfully!', 2000);
            this.loadUserData(); // Refresh data
            this.cdr.detectChanges();
          }, 2500);
        } else {
          clearInterval(progressInterval);
          this.isLogoUploading = false;
          this.showSuccessCheck = false;
          this.logoUploadProgress = 0;
          this.avatarSizeError = 'Failed to upload profile picture';
          this.cdr.detectChanges();
        }
      },
      error: (error: any) => {
        clearInterval(progressInterval);
        this.isLogoUploading = false;
        this.showSuccessCheck = false;
        this.logoUploadProgress = 0;
        this.avatarSizeError = 'Failed to upload profile picture. Please try again.';
        this.cdr.detectChanges();
      }
    });
  }

  public cancelUserAvatarEdit(): void {
    this.isUserAvatarEditMode = false;
    this.selectedUserAvatar = null;
    this.avatarSizeError = '';
    this.notificationService.showInfo('User avatar change cancelled', 2000);
  }

  public removeProfilePhoto(preview?: any): void {
    const userId = this.currentUser?.id || this.currentUser?.user_id;
    if (!userId) {
      this.notificationService.showError('User ID not found!', 3000);
      return;
    }

    this.isLoading = true;
    this.cdr.detectChanges();

    this.apiService.updateUser({
      id: userId,
      // @ts-ignore
      profile_image: null,
      // @ts-ignore
      avatarUrl: null
    }).subscribe({
      next: (response: any) => {
        this.isLoading = false;
        if (this.currentUser) {
          this.currentUser.profile_image = null;
          this.currentUser.avatarUrl = null;
        }
        this.selectedUserAvatar = null;

        // Close preview if open
        if (preview) {
          preview.complete();
        }

        // Update localStorage
        try {
          const userInfo = localStorage.getItem('user_info');
          if (userInfo) {
            const userData = JSON.parse(userInfo);
            userData.profile_image = null;
            userData.avatarUrl = null;
            localStorage.setItem('user_info', JSON.stringify(userData));
          }
        } catch (e) { }

        this.notificationService.showSuccess('Profile picture removed successfully', 2000);
        this.loadUserData(); // Refresh data from API
        this.cdr.detectChanges();
      },
      error: (error: any) => {
        this.isLoading = false;
        this.notificationService.showError('Failed to remove profile picture', 3000);
        this.cdr.detectChanges();
      }
    });
  }

  public downloadLogo(): void {
    const logoUrl = this.getUserAvatarSrc();
    if (logoUrl) {
      window.open(logoUrl, '_blank');
      this.notificationService.showSuccess('Logo opened in new tab', 2000);
    }
  }

  protected showLogoPreview(): void {
    if (this.logoPreview) {
      this.previewService.open(this.logoPreview).subscribe();
    }
  }

  protected replaceUserAvatar(preview: any): void {
    preview.complete();
    this.triggerUserAvatarFileInput();
  }

  protected get logoPreviewContent(): PolymorpheusContent {
    return this.getUserAvatarSrc();
  }



  private fetchSessions(): void {
    const user = this.apiService.getCurrentUser();
    if (!user?.id) return;

    this.loadingSessions.set(true);
    this.apiService.getUserSessions(user.id).subscribe({
      next: (response) => {
        const sessions = Array.isArray(response) ? response : (response?.data || []);
        const mappedSessions = sessions.map((s: any) => this.mapSessionToDeviceData(s));

        this.activeSessions.set(mappedSessions);
        this.loadingSessions.set(false);
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Failed to fetch sessions:', err);
        this.loadingSessions.set(false);
        this.cdr.detectChanges();
      }
    });
  }

  private mapSessionToDeviceData(session: any): DeviceData {
    let locationStr = 'Unknown Location';
    if (session.location) {
      const parts = session.location.split('|')[0].split('-');
      if (parts.length >= 3) {
        locationStr = `${parts[0]}, ${parts[2]}`;
      } else {
        locationStr = session.location.split('|')[0].trim();
      }
    }

    const deviceInfo = DeviceIdentifier.parseSessionDeviceInfo(session);

    const currentUser = this.apiService.getCurrentUser();
    const currentSessionId = currentUser?.session_id || currentUser?.sessionId;

    const isCurrent = session.id === currentSessionId || session.session_id === currentSessionId || !!session.is_current || !!session.current;

    const deviceName = DeviceIdentifier.getBestSessionDeviceName(session, deviceInfo);
    const icon = DeviceIdentifier.getDeviceIcon(session, deviceInfo, deviceName);

    return {
      id: session.id,
      session_id: session.session_id || session.id,
      device: {
        name: deviceName,
        browser: DeviceIdentifier.getSessionBrowserLabel(session, deviceInfo),
        ip: session.ip_address || '-',
        icon: icon
      },
      location: locationStr,
      firstSignIn: new Date(session.createdAt),
      lastActivity: new Date(session.last_activity_at || session.updatedAt),
      isActive: session.is_active,
      isCurrent: isCurrent
    };
  }

  protected logoutDevice(session: DeviceData): void {
    this.dialogs
      .open<boolean>(new PolymorpheusComponent(SignOutConfirmationDialog, this.injector), {
        size: 's',
        data: session,
      })
      .subscribe(confirmed => {
        if (confirmed) {
          if (session.isCurrent) {
            this.apiService.logout().subscribe({
              next: () => {
                this.notificationService.showSuccess('Logged out successfully');
                this.router.navigate(['/login']);
              },
              error: (err) => {
                this.router.navigate(['/login']);
              }
            });
            return;
          }

          const userId = this.currentUser?.id || this.currentUser?.user_id;
          this.apiService.logoutUserSession(userId, session.session_id).subscribe({
            next: () => {
              this.notificationService.showSuccess('Device logged out successfully');
              this.fetchSessions();
            },
            error: (err) => {
              this.notificationService.showError('Failed to logout device');
            }
          });
        }
      });
  }

  protected logoutAllSessions(): void {
    const userId = this.currentUser?.id || this.currentUser?.user_id;
    this.apiService.logoutAllUserSessions(userId).subscribe({
      next: () => {
        this.notificationService.showSuccess('All sessions logged out successfully');
        this.fetchSessions();
      },
      error: (err) => {
        this.notificationService.showError('Failed to logout all sessions');
      }
    });
  }

  ngOnDestroy(): void {
    if (this.otpTimer) {
      clearInterval(this.otpTimer);
    }
    // Clean up: remove modal-open class from body when component is destroyed
    document.body.classList.remove('modal-open');

    // Clean up subscription
    if (this.roleSubscription) {
      this.roleSubscription.unsubscribe();
    }
  }
}
